import { CommonModule, NgOptimizedImage, NgTemplateOutlet } from '@angular/common';
import {
  afterNextRender,
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  Input,
  LOCALE_ID,
  OnChanges,
  output,
  QueryList,
  runInInjectionContext,
  signal,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, map, Subject, switchMap } from 'rxjs';

import { ComparisonOperator, type UserInfo } from '@blaze-shared/api-contracts-domain';
import { ButtonIconComponent, ScrollerComponent } from '@blaze-shared/shared-ui';
import { runAfterFramePaint } from '@blaze-shared/shared-util';
import { CssMediaService } from '@blaze-shared/theming-data-access';

import { ChatStore } from '../chat/chat.store';
import { type ChatMessageAlignment } from '../chat-element.models';
import { ChatInfiniteScrollDirective } from '../chat-infinite-scroll.directive';
import { ChatMessageComponent } from '../chat-message/chat-message.component';
import {
  type ChatLastReadMessage,
  type ChatMember,
  ChatMemberInfo,
  ChatMessage,
  ChatMessageId,
} from '../chat.models';
import { SwipeableComponent } from '../swipeable/swipeable.component';

import { buildChatMessagesVm, ChatMessageViewModel } from './conversation.utils';

export class LoadMoreMessagesEvent {
  constructor(
    public lastMessage?: ChatMessage,
    public operator?: ComparisonOperator,
  ) {}
}

@UntilDestroy()
@Component({
  selector: 'be-conversation',
  templateUrl: './conversation.component.html',
  styleUrls: ['./conversation.component.scss'],
  imports: [
    ChatMessageComponent,
    ChatInfiniteScrollDirective,
    ScrollerComponent,
    NgOptimizedImage,
    NgTemplateOutlet,
    MatMenuModule,
    MatIcon,
    CommonModule,
    SwipeableComponent,
    ButtonIconComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConversationComponent implements AfterViewInit, OnChanges {
  private readonly chatMessageEditService = inject(ChatStore);
  private readonly cssMediaService = inject(CssMediaService);
  private readonly injector = inject(Injector);
  private readonly locale = inject(LOCALE_ID);

  isViewAsMode = input(false);

  messages = input.required<ChatMessage[]>();
  chatMembersStatuses = input<Map<string, ChatMemberInfo>>();
  messageAlignment = input<ChatMessageAlignment>('left');
  currentUser = input<UserInfo | undefined>(undefined);

  chatId = input<string>();

  lastMessageRead = output<{ id: number }>();
  loadMore = output<LoadMoreMessagesEvent>();

  @Input() allMessagesRead?: boolean;
  @Input() createdBy?: ChatMember;
  @Input() introImage?: string;
  @Input() isEditingEnabled: boolean;
  @Input() isMobile?: boolean;
  @Input() lastReadMessageForConversation: ChatLastReadMessage;
  @Input() loadMoreMessagesType: 'top' | 'both' = 'top';
  @Input() membersColors: Record<string, { foreground: string; background: string }> = {};

  @ViewChild('lastMessageReadMarker') lastMessageReadMarker!: ElementRef<HTMLDivElement>;
  @ViewChild(MatMenu) messageMenu!: MatMenu | undefined;
  @ViewChild(ScrollerComponent) scroller?: ScrollerComponent;
  @ViewChildren(MatMenuTrigger) menuTrigger!: QueryList<MatMenuTrigger>;
  @ViewChildren(SwipeableComponent) swipeableElements!: QueryList<SwipeableComponent>;

  private readonly lastMessageRead$$ = new Subject<{ id: number }>();
  private _scrollTo?: 'top' | 'bottom' | ChatMessageId = 'top';

  private readonly messageEditIdInProgress = toSignal(
    toObservable(this.chatId).pipe(
      switchMap((chatId) => this.chatMessageEditService.getEditMessage(chatId)),
      map((message) => message?.id),
    ),
    { initialValue: undefined },
  );

  private readonly firstMessage = computed(() => this.messages()[0]);
  private readonly lastMessage = computed(() => {
    const messages = this.messages();
    return messages.at(-1);
  });

  operatorGT = ComparisonOperator.Gt;
  operatorLT = ComparisonOperator.Lt;
  isTouchMode = this.cssMediaService.isTouchMode;

  scrollToId = signal<number | undefined>(undefined);
  menuOpenedMessageId: ChatMessageId | undefined;

  readonly messagesLength = computed(() => this.messages().length);

  readonly isSplitLayout = computed(() => this.messageAlignment() === 'split');

  readonly chatMessagesVm = computed<ChatMessageViewModel[]>(() =>
    buildChatMessagesVm({
      chatMembersStatuses: this.chatMembersStatuses(),
      messages: this.messages(),
      lastReadId: this.lastReadMessageForConversation.lastReadMessageId,
      allRead: !!this.allMessagesRead,
      scrollToId: this.scrollToId(),
      membersColors: this.membersColors,
      currentUserEmail: this.currentUserEmail,
      editId: this.messageEditIdInProgress(),
      locale: this.locale,
      splitLayout: this.isSplitLayout(),
    }),
  );
  readonly additionalTopSpacing = computed(() => {
    return this.chatMessagesVm()[0]?.isCurrentUserAuthor && this.isEditingEnabled ? 16 : 0;
  });

  @Input() set scrollTo(value: 'top' | 'bottom' | ChatMessageId | undefined) {
    this._scrollTo = value;
    if (typeof value === 'number' || !isNaN(Number(value))) {
      this.scrollToId.set(Number(value));
      this.scrollToMessage(this.scrollToId());
    } else {
      this.scrollToId.set(undefined);
      this.scrollToMessage(undefined);
    }
  }
  get scrollTo(): 'top' | 'bottom' | ChatMessageId | undefined {
    return this._scrollTo ?? 'bottom';
  }

  get currentUserEmail(): string | undefined {
    return this.currentUser()?.email;
  }

  get viewport(): HTMLElement | null | undefined {
    return this.scroller?.getViewport();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.scrollTo) {
      if (this.messages().some((msg) => msg.id === changes.scrollTo.currentValue)) {
        afterNextRender(
          () => {
            this.scrollToMessage(changes.scrollTo.currentValue);
          },
          { injector: this.injector },
        );
        this._scrollTo = undefined; // Reset scrollTo after scrolling to the message
      }
    }
  }

  ngAfterViewInit(): void {
    this.checkIfLastMessageRead();
    this.lastMessageRead$$
      .pipe(
        untilDestroyed(this),
        filter(({ id }) => id > -1),
      )
      .subscribe((event) => {
        this.lastMessageRead.emit(event);
      });

    this.messageMenu?.closed.pipe(untilDestroyed(this)).subscribe(() => {
      this.menuOpenedMessageId = undefined;
    });

    runInInjectionContext(this.injector, () => {
      // Reset menu open state when empty message edit state
      effect(() => {
        const editId = this.messageEditIdInProgress();
        if (editId) {
          return;
        }
        this.menuOpenedMessageId = editId;
      });

      effect(() => {
        if (!this.scroller) {
          return;
        }

        this.checkIfLastMessageRead();
        this.scrollView();
      });
    });

    this.scrollView();
  }

  emitLoadMoreMessages(operator: ComparisonOperator): void {
    if (this.loadMoreMessagesType === 'top' && operator === ComparisonOperator.Gt) {
      // Do not load more messages if the operator is Gt and loadMoreMessagesType is 'top'
      return;
    }
    const message = operator === ComparisonOperator.Lt ? this.firstMessage() : this.lastMessage();
    if (!message || this.isMessageTemporaryId(message.id)) {
      // ignore loading more messages after temporary message
      return;
    }
    this.loadMore.emit(new LoadMoreMessagesEvent(message, operator));
  }

  checkIfLastMessageRead(): void {
    if (this.messagesLength() > 0) {
      const lastMessage = this.lastMessage();
      // do not emit temp ids (strings) from the optimistic update
      if (typeof lastMessage?.id === 'number') {
        this.lastMessageRead$$.next({ id: lastMessage.id });
      }
    }
  }

  editMessage(messageId: ChatMessageId): void {
    const chatId = this.chatId();

    if (this.isViewAsMode() || !chatId) {
      return;
    }

    if (this.isMobile) {
      this.closeSwipeableElements(true);
    }

    const message = this.messages().find(
      (message) => message.id === (messageId ?? this.menuOpenedMessageId),
    );

    this.chatMessageEditService.setEditMessage({ chatId, message });
  }

  closeSwipeableElements(opened?: boolean, openedElement?: SwipeableComponent): void {
    if (!opened) {
      return;
    }
    this.swipeableElements.forEach((el) => {
      if (el !== openedElement) {
        el.close();
      }
    });
  }

  private scrollView(): void {
    const viewport = this.viewport;
    if (!viewport) {
      return;
    }
    const height = viewport.scrollHeight;
    runAfterFramePaint(() => {
      if (this.scrollTo === 'top') {
        viewport.scrollTop = viewport.scrollHeight - height;
      } else if (this.scrollTo === 'bottom') {
        viewport.scrollTop = viewport.scrollHeight;
      } else {
        this.scrollToMessage(this.scrollToId());
        const scrollToIndex = Number(this.scrollTo);
        if (
          scrollToIndex &&
          this.messages().some((m) => typeof m.id === 'number' && m.id < scrollToIndex)
        ) {
          // Reset scrollTo when messages above are loaded
          this._scrollTo = undefined;
        }
      }
    });
  }

  private scrollToMessage(id?: number): void {
    const viewport = this.scroller?.getViewport();
    const element = viewport?.querySelector(`[data-message-id="${id}"]`) as HTMLElement | null;
    if (!viewport || !element) {
      return;
    }

    const elementTop = element.getBoundingClientRect().top;
    const viewportTop = viewport.getBoundingClientRect().top;

    viewport.scrollTop += elementTop - viewportTop;
  }

  private isMessageTemporaryId(id?: ChatMessageId): boolean {
    return typeof id === 'string' && id.startsWith('temp_');
  }
}
